'use client';

import React, { useState } from 'react';
import LoginForm from './LoginForm';
import SignupForm from './SignupForm';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

const features = [
  { key: 'feat-prestamos', icon: 'CreditCardIcon', label: 'Gestión de Préstamos', desc: 'Controla capital, intereses, mora y cobros' },
  { key: 'feat-studio', icon: 'FilmIcon', label: 'EGA Studio CRM', desc: 'Pipeline de clientes audiovisuales' },
  { key: 'feat-music', icon: 'MusicalNoteIcon', label: 'EGA Studio Music', desc: 'Producción y distribución musical' },
  { key: 'feat-ventas', icon: 'ShoppingCartIcon', label: 'Control de Ventas', desc: 'Productos, pedidos e inventario' },
  { key: 'feat-finanzas', icon: 'BanknotesIcon', label: 'Finanzas Personales', desc: 'Ingresos, gastos y metas financieras' },
  { key: 'feat-productividad', icon: 'ChartBarIcon', label: 'Motor de Productividad', desc: 'Prioriza actividades que generan ingresos' },
];

export default function AuthPageClient() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative flex-col justify-between p-10 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-background" />
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

        <div className="relative z-10">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={48} />
            <div>
              <span className="block text-xl font-700 text-foreground tracking-tight">EGA AGENDA PRO</span>
              <span className="block text-xs text-muted-foreground tracking-widest uppercase">Centro de Control Empresarial</span>
            </div>
          </div>

          {/* Hero text */}
          <div className="mb-10">
            <h1 className="text-3xl xl:text-4xl font-700 text-foreground leading-tight mb-4">
              Organiza tu tiempo.<br />
              <span className="text-accent">Maximiza tus ingresos.</span>
            </h1>
            <p className="text-base text-muted-foreground max-w-md leading-relaxed">
              Controla tus préstamos, clientes, negocios y finanzas desde un solo lugar.
              Cada mañana sabrás exactamente qué hacer para generar más dinero.
            </p>
          </div>

          {/* Features grid */}
          <div className="grid grid-cols-2 gap-3">
            {features.map((f) => (
              <div key={f.key} className="flex items-start gap-3 p-3 rounded-lg bg-card/40 border border-border/50 backdrop-blur-sm">
                <div className="w-8 h-8 rounded-md bg-primary/15 flex items-center justify-center flex-shrink-0">
                  <Icon name={f.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-600 text-foreground truncate">{f.label}</p>
                  <p className="text-xs text-muted-foreground leading-snug">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom stat */}
        <div className="relative z-10 flex items-center gap-6 pt-6 border-t border-border/30">
          {[
            { key: 'stat-negocios', label: '4+ Negocios', desc: 'controlados' },
            { key: 'stat-currency', label: 'Lempiras (L)', desc: 'moneda nativa' },
            { key: 'stat-modules', label: '12 Módulos', desc: 'integrados' },
          ].map((stat) => (
            <div key={stat.key}>
              <p className="text-sm font-700 text-foreground">{stat.label}</p>
              <p className="text-xs text-muted-foreground">{stat.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-10">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 justify-center mb-8 lg:hidden">
            <AppLogo size={36} />
            <span className="text-lg font-700 text-foreground">EGA AGENDA PRO</span>
          </div>

          {/* Mode toggle */}
          <div className="flex bg-secondary rounded-lg p-1 mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 text-sm font-600 rounded-md transition-all duration-200 ${
                mode === 'login' ?'bg-primary text-primary-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Iniciar Sesión
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2 text-sm font-600 rounded-md transition-all duration-200 ${
                mode === 'signup' ?'bg-primary text-primary-foreground shadow-sm' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Crear Cuenta
            </button>
          </div>

          {/* Forms */}
          <div className="animate-fade-in">
            {mode === 'login' ? (
              <LoginForm onSwitchToSignup={() => setMode('signup')} />
            ) : (
              <SignupForm onSwitchToLogin={() => setMode('login')} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}